import { useStoreManagementStore } from "@/store/storeManagementStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { Store } from "@/models/storeModel";
import { useTranslation } from "react-i18next";

interface AddStoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddStoreDialog({ open, onOpenChange }: AddStoreDialogProps) {
  const { t } = useTranslation();
  const { addStore } = useStoreManagementStore();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      email: "",
    },
  });

  const onSubmit = async (data: any) => {
    try {
      // Creating a store with the minimal required information
      const storeData: Omit<Store, "id" | "created_at" | "updated_at"> = {
        name: data.name,
        address: data.address,
        phone: data.phone,
        email: data.email,
        is_active: true,
      };

      await addStore(storeData);
      toast({
        title: t("stores.addSuccess.title"),
        description: t("stores.addSuccess.description"),
      });
      reset();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Failed to add store:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("stores.addError"),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("stores.add.title")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t("stores.fields.name")}</Label>
            <Input
              id="name"
              placeholder={t("stores.placeholders.name")}
              {...register("name", {
                required: t("stores.validation.nameRequired"),
              })}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="address">{t("stores.fields.address")}</Label>
            <Input
              id="address"
              placeholder={t("stores.placeholders.address")}
              {...register("address", {
                required: t("stores.validation.addressRequired"),
              })}
            />
            {errors.address && (
              <p className="text-sm text-destructive">
                {errors.address.message}
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="phone">{t("stores.fields.phone")}</Label>
            <Input
              id="phone"
              placeholder={t("stores.placeholders.phone")}
              {...register("phone", {
                required: t("stores.validation.phoneRequired"),
              })}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">{t("stores.fields.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("stores.placeholders.email")}
              {...register("email", {
                required: t("stores.validation.emailRequired"),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("stores.validation.emailInvalid"),
                },
              })}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("stores.add.adding") : t("stores.add.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
